import React, { useState } from "react";

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  AlertTitle,
  Avatar,
  Box,
  Button,
  Grid,
  Link as MUILink,
  Skeleton,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import imageLightning from "assets/images/lightning.png";
import imageSats from "assets/images/sats.png";
import Card2 from "components/elements/Card2";
import CustomAlert from "components/elements/CustomAlert";
import MaskedModeWrapper from "components/elements/MaskedModeWrapper";
import SectionCard from "components/elements/SectionCard";
import { Wallet } from "components/icons";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useUserDataContext } from "context/UserDataContext";
import { useAPICashbackSats } from "hooks/useAPI";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { renderNumber } from "utils/formatter";

import HistoryModal from "./HistoryModal";
import Transactions from "./Transactions";
import UtilizationsModal from "./UtilizationsModal";

const InfoCardButton = styled(Button)({
  padding: 0,
  "&:hover": {
    backgroundColor: "transparent",
  },
});

const infoCardIcon = (key: string) => {
  switch (key) {
    case "earned":
      return <Avatar src={imageSats} sx={{ width: "24px", height: "24px" }} />;
    case "utilized":
      return <Wallet />;
    case "available":
      return <Avatar src={imageLightning} sx={{ width: "24px", height: "24px" }} />;
  }
};

const infoCardButtonDisabled = (key: string, value: number) => {
  switch (key) {
    case "utilized":
      return value <= 0;
    default:
      return false;
  }
};

interface IProps {
  hasExchangeConnection: boolean;
}

const CashbackSatsList: React.FC<IProps> = ({ hasExchangeConnection }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useMUIThemeModeContext();
  const { data, mutate: mutateCashbackSats } = useAPICashbackSats();
  const { activeCashbackSats } = useUserDataContext();
  const [isOpenUtilizationsModal, setIsOpenUtilizationsModal] = useState(false);
  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false);

  const earned = data?.earned || 0;
  const utilized = data?.utilized || 0;
  const available = data?.available || 0;

  if (!hasExchangeConnection && earned === 0) return null;

  const onInfoCardClick = (key: string) => {
    if (key === "earned") navigate("/proc-bitcoin/cashback-saty");
    if (key === "available") setIsOpenUtilizationsModal(true);
    if (key === "utilized") setIsOpenHistoryModal(true);
  };

  const infoCardValueColor = (key: string) => {
    switch (key) {
      case "earned":
        return "tertiary.main";
      case "utilized":
        return colors.black;
      case "available":
        return colors.success;
    }
  };

  const refetch = () => {
    mutateCashbackSats();
  };

  const infoCard = (key: string, value: number) => {
    return (
      <Card2 height={"100%"}>
        <Stack spacing={{ xs: 1, md: 3 }}>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Stack>
              <Typography fontSize={"0.75rem"} color={colors.gray500}>
                {t(`cashbackSatsList.${key}.title`)}
              </Typography>
              <MaskedModeWrapper fontSize={"1.5rem"} color={infoCardValueColor(key)} fontWeight={500}>
                <Typography fontSize={"1.5rem"} color={infoCardValueColor(key)} fontWeight={500}>
                  {renderNumber(value)}&nbsp;{t(`cashbackSatsList.sats`, { count: value })}
                </Typography>
              </MaskedModeWrapper>
            </Stack>
            {infoCardIcon(key)}
          </Stack>
          <Box>
            <InfoCardButton
              endIcon={key !== "earned" && <ChevronRightIcon />}
              disabled={infoCardButtonDisabled(key, value)}
              onClick={() => onInfoCardClick(key)}>
              {t(`cashbackSatsList.${key}.button`)}
            </InfoCardButton>
          </Box>
        </Stack>
      </Card2>
    );
  };

  const getUpsellAlert = () => {
    if (activeCashbackSats === undefined || activeCashbackSats) return null;

    return (
      <Box pb={2}>
        <CustomAlert severity="premium">
          <AlertTitle>
            <Trans i18nKey={"cashbackSatsList.upsell.title"} />
          </AlertTitle>
          <Trans
            i18nKey={"cashbackSatsList.upsell.description"}
            components={{
              a1: <Link to={"/stosaky#predplatne"} style={{ color: "inherit" }} />,
              a2: <MUILink href={"/proc-bitcoin/cashback-saty"} color={"inherit"} />,
            }}
          />
        </CustomAlert>
      </Box>
    );
  };

  return (
    <SectionCard id={"cashback-saty"} title={t("cashbackSatsList.title") + " 🧡"}>
      {getUpsellAlert()}
      {!data && (
        <Grid container columnSpacing={3}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="text" height={150} width={"100%"} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="text" height={150} width={"100%"} />
          </Grid>
          <Grid item xs={12} md={4}>
            <Skeleton variant="text" height={150} width={"100%"} />
          </Grid>
        </Grid>
      )}
      {data?.cashbackSats?.length === 0 ? (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4}>
            {infoCard("earned", earned)}
          </Grid>
          <Grid item xs={12} lg={4}>
            {infoCard("utilized", utilized)}
          </Grid>
          <Grid item xs={12} lg={4}>
            {infoCard("available", available)}
          </Grid>
        </Grid>
      ) : null}
      {data?.cashbackSats?.length ? (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4} xl={3}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {infoCard("earned", earned)}
              </Grid>
              <Grid item xs={12}>
                {infoCard("utilized", utilized)}
              </Grid>
              <Grid item xs={12}>
                {infoCard("available", available)}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={8} xl={9}>
            <Transactions data={data} />
          </Grid>
        </Grid>
      ) : null}
      {isOpenUtilizationsModal && (
        <UtilizationsModal onClose={() => setIsOpenUtilizationsModal(false)} refresh={refetch} available={available} />
      )}
      {isOpenHistoryModal && (
        <HistoryModal onClose={() => setIsOpenHistoryModal(false)} utilized={utilized} />
      )}
    </SectionCard>
  );
};

export default CashbackSatsList;
